import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  TemplateRef,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { SpecialRequestItem, SpecialRequestType, SpecialRequestsRoom } from '@appTypes/api.types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StarsComponent } from '@shared/components/stars/stars.component';
import { DynamicModalRef, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { displayTraceId } from '@core/interceptors/trace-context';
import {
  SpecialRequestsApiService,
  SpecialRequestsError,
  toSpecialRequestsError
} from './special-requests-api.service';
import { SpecialRequestsErrorBoxComponent } from './special-requests-error-box.component';
import { REQUEST_ICON_PATHS, SpecialRequestIcon, requestIconFor } from './special-requests-icons';
import {
  BOOKING_NUMBER_MAX,
  SpecialRequestsDraft,
  SpecialRequestsLinkKind,
  SpecialRequestsOtherRoom,
  bookingNumberRefused,
  itemName,
  popupItems,
  roomSummary,
  toRoomIn
} from './special-requests.util';

/**
 * The booking summary card at the top of the dialog (SRDIALOG-001, R-SDA3 / R-SDA4). Checkout: name, stars, dates, nights and
 * room · meal · accommodation; reservation page: the hotel name and the service description only. The caller formats the texts.
 */
export interface SpecialRequestsCard {
  title: string;
  /** hotel class as the cart row has it ("5"); nothing on the reservation page */
  stars?: string | null;
  /** "dd.mm.yyyy - dd.mm.yyyy" */
  dates?: string | null;
  nights?: number | null;
  /** room · meal · accommodation (checkout) or the service description (reservation page) */
  meta: string[];
}

/** What the caller hands the popup (checkout: keep the draft on the page; reservation page: PUT at once). */
export interface SpecialRequestsPopupData {
  mode: 'checkout' | 'reservation';
  roomNo: number;
  /** hotel · room · occupancy · period, already formatted by the caller */
  roomLine: string;
  /** the summary card; without it the card shows `roomLine` as its title */
  card?: SpecialRequestsCard;
  /** the ACTIVE list in operator order (R-SR6) */
  types: SpecialRequestType[];
  /** the items the room carries now - a retired one stays ticked (R-SR19g) */
  chosen?: SpecialRequestItem[];
  draft: SpecialRequestsDraft;
  otherRooms: SpecialRequestsOtherRoom[];
  rfid?: string;
  roomId?: string;
  rowVersion?: string | null;
  /** this reservation's own number - refused as a link on the reservation page (R-SR10) */
  ownBookingNumber?: string | null;
  /** called when the PUT succeeded, also when the popup was closed while it ran (only the newest answer is drawn, R-SR19f) */
  onSaved?: (room: SpecialRequestsRoom) => void;
  /** called when a Save was refused as REQUESTS_CHANGED: the caller's copy of the room is stale (SRDIALOG-002, R-SDA20); once the popup has re-read the room it hands the fresh room along, so the page can show it at once */
  onChanged?: (fresh?: SpecialRequestsRoom) => void;
}

export type SpecialRequestsPopupResult =
  | { kind: 'draft'; draft: SpecialRequestsDraft }
  | { kind: 'saved'; room: SpecialRequestsRoom }
  | { kind: 'locked'; error: SpecialRequestsError }
  | { kind: 'flagOff'; error: SpecialRequestsError }
  | { kind: 'forbidden'; error: SpecialRequestsError };

type PopupError =
  | { box: 'save'; error: SpecialRequestsError; message: string; canRetry: boolean }
  | { box: 'changed'; error: SpecialRequestsError }
  | { box: 'rate'; error: SpecialRequestsError };

/**
 * How the dialog opens (SRDIALOG-001, R-SDA14): 640 px wide (capped to the window minus 32 px) on a desktop / tablet; on a phone the
 * shared dynamic modal's own slide-up sheet (`mode: 'auto'`, the site's `sm` breakpoint = below 768 px), made full width by the
 * `.sr-dialog` rule in styles.scss. No frame header (no `modalTitle`): the popup draws its own with the Room n chip and the X, which
 * is also the swipe handle; the dialog is named by that title.
 */
export const specialRequestsDialog = (roomNo: number) => ({
  width: '640px',
  maxWidth: 'calc(100vw - 32px)',
  height: 'auto',
  mode: 'auto' as const,
  autoModeMaxBreakPoint: 'sm' as const,
  styleClass: 'sr-dialog',
  dialogConfig: { ariaLabelledBy: `special-requests-popup-title-${roomNo}` }
});

const SAVE_FAILED = 'Special requests were not saved. Your choice is kept - try again.';

/**
 * The "Special requests · Room n" popup (QA doc docs features/booking-special-requests/qa.md R-SR6 .. R-SR11, R-SR14, R-SR16,
 * R-SR19b .. R-SR19d, R-SR19f, R-SR19g; prototype mockups/01-website-prototype.html `popupEl`). Built on the website dynamic modal.
 * Checkout: **Save** closes with the draft (nothing is sent before **Register the reservation**). Reservation page: **Save** PUTs the
 * room; every refusal shows its coded box with the **Trace ID** and the typed choice stays (C3 / C17 / C23).
 */
@Component({
  selector: 'app-special-requests-popup',
  templateUrl: './special-requests-popup.component.html',
  styleUrl: './special-requests-popup.component.scss',
  imports: [TranslatePipe, MatButtonModule, SpecialRequestsErrorBoxComponent, StarsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpecialRequestsPopupComponent implements OnDynamicModal<
  SpecialRequestsPopupData,
  SpecialRequestsPopupResult
> {
  readonly #api = inject(SpecialRequestsApiService);
  readonly #translate = inject(TranslateService);
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);
  readonly #clipboard = inject(Clipboard);

  readonly iconPaths = REQUEST_ICON_PATHS;
  readonly bookingMax = BOOKING_NUMBER_MAX;

  readonly data = signal<SpecialRequestsPopupData | null>(null);
  readonly draft = signal<SpecialRequestsDraft>({
    typeIds: [],
    otherWishes: '',
    linkKind: 'none',
    linkRoomKey: null,
    bookingNumber: ''
  });
  readonly saving = signal(false);
  readonly error = signal<PopupError | null>(null);
  /** the inline link refusal: local (no Trace ID) or the server's 400 LINK_INVALID (with it) */
  readonly linkError = signal<SpecialRequestsError | null>(null);
  /** the server's Trace ID of that refusal, shown with **Copy** like every other box of the dialog (website-web#197, R-5) */
  readonly linkTrace = computed(() => displayTraceId(this.linkError()?.traceId));
  /** the Trace ID last copied; a new refusal has another id, so its button reads **Copy** again */
  readonly linkCopied = signal<string | null>(null);
  /** R-SR19c: what is stored now, after a REQUESTS_CHANGED */
  readonly current = signal<string | null>(null);
  readonly #rowVersion = signal<string | null>(null);

  readonly lang = signal<string>(this.#translate.getCurrentLang() ?? '');
  readonly items = computed(() => {
    const d = this.data();
    return d ? popupItems(d.types, d.chosen ?? []) : [];
  });
  readonly roomNo = computed(() => this.data()?.roomNo ?? 0);
  readonly base = computed(() => `special-requests-popup-${this.roomNo()}`);
  /** NOT `modalTitle`: the dialog frame would draw its own header; this popup draws its own (title + Room n chip + X, R-SDA1) */
  readonly dialogName = computed(() => {
    this.lang();
    return this.#translate.instant('Special requests · Room {n}', { n: this.roomNo() }) as string;
  });
  /** the dialog's accessible name: "Special requests · Room n" (the visible header splits it into title + chip) */
  readonly titleId = computed(() => `special-requests-popup-title-${this.roomNo()}`);
  readonly card = computed<SpecialRequestsCard>(() => {
    const d = this.data();
    return d?.card ?? { title: d?.roomLine ?? '', meta: [] };
  });
  /** the card's stars only for a class the star component can draw (0..6); anything else draws no stars */
  readonly stars = computed(() => {
    const c = this.card().stars?.trim();
    return c && /^[0-6]$/.test(c) && c !== '0' ? c : null;
  });
  /** R-SDA13: ticked items only; a link does not count */
  readonly selectedCount = computed(() => this.draft().typeIds.length);
  /** R-SDA7: the room's stored other wishes, shown read-only */
  readonly storedWishes = computed(() => this.draft().otherWishes.trim());

  readonly modalFooterTemplateRef = viewChild.required<TemplateRef<unknown>>('modalFooterTemplateRef');
  modalRef?: DynamicModalRef;
  onDynamicModalClose?: (result?: SpecialRequestsPopupResult) => void;

  constructor() {
    this.#translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((e) => this.lang.set(e.lang));
    // R-SR19f / R-SDA13: the shared modal's swipe-down close calls close() directly (it ignores disableClose), so while a save runs the
    // touch never reaches the header's gesture handler: stopped in the capture phase on this popup's own host element
    this.#host.nativeElement.addEventListener('touchstart', (ev) => this.saving() && ev.stopPropagation(), {
      capture: true
    });
    afterNextRender(() => {
      const first = this.#host.nativeElement.querySelector<HTMLElement>('input');
      first?.focus();
    });
  }

  onDynamicModal(data: SpecialRequestsPopupData): void {
    this.data.set(data);
    this.draft.set({ ...data.draft, typeIds: [...data.draft.typeIds] });
    this.#rowVersion.set(data.rowVersion ?? null);
  }

  name(item: Pick<SpecialRequestType, 'nameEn' | 'nameRu'>): string {
    return itemName(item, this.lang());
  }

  /** R-SDA5: the tile's icon, chosen by a word in the English name only */
  iconOf(item: Pick<SpecialRequestType, 'nameEn'>): SpecialRequestIcon {
    return requestIconFor(item.nameEn);
  }

  ticked(typeId: number): boolean {
    return this.draft().typeIds.includes(typeId);
  }

  toggle(typeId: number, on: boolean): void {
    this.draft.update((d) => ({
      ...d,
      typeIds: on ? [...d.typeIds.filter((x) => x !== typeId), typeId] : d.typeIds.filter((x) => x !== typeId)
    }));
  }

  setLinkKind(kind: SpecialRequestsLinkKind): void {
    this.linkError.set(null);
    const others = this.data()?.otherRooms ?? [];
    this.draft.update((d) => {
      if (kind !== 'room') return { ...d, linkKind: kind };
      const keep = !!d.linkRoomKey && others.some((o) => o.key === d.linkRoomKey);
      return { ...d, linkKind: kind, linkRoomKey: keep ? d.linkRoomKey : (others[0]?.key ?? null) };
    });
  }

  setLinkRoom(key: string): void {
    this.draft.update((d) => ({ ...d, linkRoomKey: key || null }));
  }

  setBookingNumber(value: string): void {
    this.linkError.set(null);
    this.draft.update((d) => ({ ...d, bookingNumber: value.slice(0, BOOKING_NUMBER_MAX) }));
  }

  /** DOM event adapters for the template (no `$any` there). */
  onToggle(typeId: number, event: Event): void {
    this.toggle(typeId, (event.target as HTMLInputElement).checked);
  }

  onLinkRoom(event: Event): void {
    this.setLinkRoom((event.target as HTMLSelectElement).value);
  }

  onBookingNumber(event: Event): void {
    this.setBookingNumber((event.target as HTMLInputElement).value);
  }

  cancel(): void {
    if (this.saving()) return;
    this.onDynamicModalClose?.();
  }

  save(): void {
    const data = this.data();
    if (!data || this.saving()) return;
    const d = this.draft();
    const own = data.mode === 'reservation' ? data.ownBookingNumber : null;
    if (d.linkKind === 'booking' && bookingNumberRefused(d.bookingNumber, own)) {
      this.linkError.set({ code: 'LINK_INVALID', traceId: null });
      this.#focus('[data-testid="room-link-booking-input"]');
      return;
    }
    if (d.linkKind === 'room' && !d.linkRoomKey) {
      this.draft.update((x) => ({ ...x, linkKind: 'none' }));
    }
    if (data.mode === 'checkout') {
      this.onDynamicModalClose?.({ kind: 'draft', draft: this.draft() });
      return;
    }
    this.#put(data);
  }

  #put(data: SpecialRequestsPopupData): void {
    if (!data.rfid || !data.roomId) return;
    // R-SDA18: the old refusal goes away while the new Save runs
    this.error.set(null);
    this.linkError.set(null);
    this.saving.set(true);
    if (this.modalRef) this.modalRef.disableClose = true;
    // not bound to the popup's life: a save that ends after the popup was closed still reaches the page (R-SR19f)
    this.#api.saveRoom(data.rfid, data.roomId, toRoomIn(this.draft(), this.#rowVersion())).subscribe({
      next: (room) => {
        this.#idle();
        data.onSaved?.(room);
        this.onDynamicModalClose?.({ kind: 'saved', room });
      },
      error: (e: unknown) => this.#refused(data, toSpecialRequestsError(e, 'REQUESTS_UNAVAILABLE'))
    });
  }

  #refused(data: SpecialRequestsPopupData, error: SpecialRequestsError): void {
    this.#idle();
    switch (error.code) {
      case 'REQUESTS_LOCKED':
        this.onDynamicModalClose?.({ kind: 'locked', error });
        return;
      case 'FEATURE_FLAG_OFF':
        this.onDynamicModalClose?.({ kind: 'flagOff', error });
        return;
      case 'RESERVATION_NOT_FOUND':
      case 'ROOM_NOT_FOUND':
        this.onDynamicModalClose?.({ kind: 'forbidden', error });
        return;
      case 'LINK_INVALID':
        this.error.set(null);
        this.linkError.set(error);
        this.#focus('[data-testid="room-link-booking-input"]');
        return;
      case 'TOO_MANY_REQUESTS':
        this.error.set({ box: 'rate', error });
        return;
      case 'REQUESTS_CHANGED':
        this.error.set({ box: 'changed', error });
        data.onChanged?.();
        this.#reload(data);
        return;
      case 'REQUEST_TYPE_NOT_AVAILABLE':
        this.error.set({ box: 'save', error, message: 'This item is no longer offered - untick it.', canRetry: false });
        return;
      case 'OTHER_WISHES_TOO_LONG':
      case 'REQUEST_INVALID':
        this.error.set({ box: 'save', error, message: SAVE_FAILED, canRetry: false });
        return;
      default:
        this.error.set({ box: 'save', error, message: SAVE_FAILED, canRetry: true });
    }
  }

  /** R-SR19c: read the stored state again - its summary becomes the **Current** line, its row version goes with the next Save. */
  #reload(data: SpecialRequestsPopupData): void {
    if (!data.rfid) return;
    // bound to the popup's life (R-SDA18); the Save PUT above is not
    this.#api
      .get(data.rfid)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (r) => {
          const room = r.rooms.find((x) => x.roomId === data.roomId);
          if (!room) return;
          this.#rowVersion.set(room.rowVersion ?? null);
          data.onChanged?.(room);
          // R-SDA10: the agent cannot type the text any more, so the next Save sends what is stored NOW (not an operator's old copy)
          this.draft.update((d) => ({ ...d, otherWishes: room.otherWishes ?? '' }));
          const text = roomSummary(room, this.lang(), (k, p) => this.#translate.instant(k, p) as string);
          this.current.set(text || (this.#translate.instant('None') as string));
        },
        error: () => this.current.set(null)
      });
  }

  #idle(): void {
    this.saving.set(false);
    if (this.modalRef) this.modalRef.disableClose = false;
  }

  copyLinkTrace(id: string): void {
    if (this.#clipboard.copy(id)) this.linkCopied.set(id);
  }

  /** website-web#196: after the next render - a server refusal ends the save, but until the page is drawn again the field still sits in
   * the fieldset disabled while saving, and focus() on it does nothing */
  #focus(selector: string): void {
    afterNextRender(() => this.#host.nativeElement.querySelector<HTMLElement>(selector)?.focus(), {
      injector: this.#injector
    });
  }
}
